import { Button, Stack } from '@mui/material';
import { LockResetRounded } from '@mui/icons-material';
import TextField from '@/components/ui/form-inputs/TextField';
import { USER_FORM_KEY } from '@/shared/constants/form-keys';
import { ChangeEvent, Fragment, ReactNode, useMemo } from 'react';
import SelectField from '@/components/ui/form-inputs/SelectField';
import { Role } from '@/core/entities';
import { CustomerSiteOption } from '@/api/customers/types';
import { Roles } from '@/core/types';
import SiteSelectField from './site-select/SiteSelectField';
import { UserFormModel } from './types';
import { SiteSelectType } from './site-select/types';

interface UserFormFieldsProps {
	formModel: UserFormModel;
	updateFormModel: (value: UserFormModel) => void;
	isAdminForm?: boolean;
	isEmailDisabled?: boolean;
	roleOptions?: Role[];
	siteField?: {
		type: SiteSelectType;
		siteOptions?: CustomerSiteOption[];
	};
	/** Operator codes are assigned by the API on create, so the field is edit-only. */
	canEditPin?: boolean;
	isOperator: boolean;
	guidance?: ReactNode;
	/** Present only when editing an existing user. */
	onSendTemporaryPassword?: () => void;
}

const UserFormFields = ({
	formModel,
	updateFormModel,
	isAdminForm,
	isEmailDisabled,
	roleOptions,
	siteField,
	canEditPin,
	isOperator,
	guidance,
	onSendTemporaryPassword,
}: UserFormFieldsProps) => {
	const { siteOptions, type: siteFieldType } = siteField || {};

	const isAccountAdminOption = (roleId: string) =>
		roleId === roleOptions?.find((role) => role.title === Roles.ACCOUNT_ADMIN)?.id;

	const accountAdminRoleSelected = useMemo(
		() => isAccountAdminOption(formModel.roleId),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[formModel.roleId, roleOptions]
	);

	// The operator types this code on the tablet, so a partial code saves nothing useful.
	// Surfaced through the same slot the API's 422 field errors use.
	const pinClientError = useMemo(
		() =>
			formModel.pin && formModel.pin.length !== 5 ? 'PIN must be exactly 5 digits' : undefined,
		[formModel.pin]
	);

	const onRoleUpdate = (event: ChangeEvent<HTMLInputElement>) => {
		const sites = isAccountAdminOption(event.target.value)
			? siteOptions?.map((option) => option.id)
			: formModel.sites;

		updateFormModel({
			...formModel,
			roleId: event.target.value,
			sites,
		});
	};

	return (
		<Stack direction="column" spacing={2}>
			<TextField
				id="firstName"
				name="firstName"
				label="First name"
				formKey={USER_FORM_KEY}
				required
				fullWidth
				value={formModel.firstName}
				onChange={(event: ChangeEvent<HTMLInputElement>) =>
					updateFormModel({ ...formModel, firstName: event.target.value })
				}
			/>
			<TextField
				id="lastName"
				name="lastName"
				label="Last name"
				formKey={USER_FORM_KEY}
				required
				fullWidth
				value={formModel.lastName}
				onChange={(event: ChangeEvent<HTMLInputElement>) =>
					updateFormModel({ ...formModel, lastName: event.target.value })
				}
			/>
			<TextField
				id="email"
				name="email"
				label="Email address"
				type="email"
				formKey={USER_FORM_KEY}
				disabled={isEmailDisabled}
				fullWidth
				value={formModel?.email ?? ''}
				onChange={(event: ChangeEvent<HTMLInputElement>) =>
					updateFormModel({ ...formModel, email: event.target.value })
				}
			/>
			{!isAdminForm && (
				<Fragment>
					<SelectField
						id="role"
						name="role"
						label="Role"
						formKey={USER_FORM_KEY}
						required
						fullWidth
						value={formModel?.roleId ?? ''}
						onChange={onRoleUpdate}
						options={roleOptions?.map((role) => ({ value: role.id, label: role.title }))}
						disabled={isOperator}
					/>
					<SiteSelectField
						type={siteFieldType || SiteSelectType.MULTISELECT}
						formModel={formModel}
						updateFormModel={updateFormModel}
						sites={siteOptions}
						disabled={accountAdminRoleSelected}
					/>
				</Fragment>
			)}
			{canEditPin && (
				<TextField
					id="pin"
					name="pin"
					label="PIN (5 digits)"
					formKey={USER_FORM_KEY}
					fullWidth
					inputProps={{ inputMode: 'numeric', maxLength: 5 }}
					clientErrors={pinClientError}
					value={formModel.pin ?? ''}
					onChange={(event: ChangeEvent<HTMLInputElement>) =>
						updateFormModel({
							...formModel,
							pin: event.target.value.replace(/\D/g, '').slice(0, 5),
						})
					}
				/>
			)}
			{canEditPin && onSendTemporaryPassword && (
				<Button
					variant="outlined"
					startIcon={<LockResetRounded />}
					onClick={onSendTemporaryPassword}
				>
					Send temporary password
				</Button>
			)}
			{guidance}
		</Stack>
	);
};

export default UserFormFields;
